import type { Timestamp } from 'firebase/firestore';
import { shuffle } from '@/utils/random';

// ==================== Audio Track Types ====================

/**
 * Source of the audio track
 */
export type AudioTrackSource = 'library' | 'chat' | 'favorites';

/**
 * Base audio track interface (used in AudioContext)
 */
export interface AudioTrack {
  id: string;
  url: string;
  title: string;
  fileName?: string;
  mimeType?: string;
  thumbnailUrl?: string;
  source: AudioTrackSource;
}

// ==================== Playlist Types ====================

/**
 * Track stored within a playlist (includes playlist-specific metadata)
 */
export interface PlaylistTrack {
  id: string; // Unique ID within playlist (for drag-drop key)
  trackId: string; // Original track ID from source
  url: string;
  title: string;
  fileName?: string;
  mimeType?: string;
  thumbnailUrl?: string;
  source: AudioTrackSource;
  addedAt: Timestamp;
}

/**
 * Playlist stored in Firestore
 */
export interface Playlist {
  id: string;
  name: string;
  description?: string;
  tracks: PlaylistTrack[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
  trackCount: number;
}

/**
 * Input for creating a new playlist
 */
export interface CreatePlaylistInput {
  name: string;
  description?: string;
  tracks?: AudioTrack[];
}

/**
 * Input for updating a playlist
 */
export interface UpdatePlaylistInput {
  name?: string;
  description?: string;
  tracks?: PlaylistTrack[];
}

// ==================== Queue & Playback Types ====================

/**
 * Repeat mode options
 */
export type RepeatMode = 'off' | 'all' | 'one';

/**
 * Queue state persisted to localStorage
 */
export interface PersistedQueueState {
  queue: AudioTrack[];
  currentIndex: number;
  currentTime: number;
  volume: number;
  playbackRate: number;
  shuffleMode: boolean;
  repeatMode: RepeatMode;
  activePlaylistId?: string; // If playing from a saved playlist
  shuffledIndices?: number[]; // Original indices in shuffled order
  timestamp: number; // For staleness check
}

/**
 * Position to add a track to the queue
 */
export type QueuePosition = 'next' | 'end' | 'now';

/**
 * Action options when user clicks play on a track
 */
export interface TrackActionOption {
  id: QueuePosition | 'playlist';
  label: string;
  icon: React.ReactNode;
  description?: string;
}

// ==================== Helper Functions ====================

/**
 * Convert an AudioTrack to a PlaylistTrack
 * Filters out undefined values to avoid Firestore errors
 */
export function audioTrackToPlaylistTrack(track: AudioTrack, addedAt: Timestamp): PlaylistTrack {
  const playlistTrack: PlaylistTrack = {
    id: crypto.randomUUID(),
    trackId: track.id,
    url: track.url,
    title: track.title,
    source: track.source,
    addedAt,
  };

  // Only include optional fields if they are defined
  if (track.fileName !== undefined) {
    playlistTrack.fileName = track.fileName;
  }
  if (track.mimeType !== undefined) {
    playlistTrack.mimeType = track.mimeType;
  }
  if (track.thumbnailUrl !== undefined) {
    playlistTrack.thumbnailUrl = track.thumbnailUrl;
  }

  return playlistTrack;
}

/**
 * Convert a PlaylistTrack back to an AudioTrack
 */
export function playlistTrackToAudioTrack(track: PlaylistTrack): AudioTrack {
  return {
    id: track.trackId,
    url: track.url,
    title: track.title,
    fileName: track.fileName,
    mimeType: track.mimeType,
    thumbnailUrl: track.thumbnailUrl,
    source: track.source,
  };
}

export interface GetNextTrackIndexParams {
  shuffleMode: boolean;
  repeatMode: RepeatMode;
  currentIndex: number;
  shuffledIndices: number[];
  queueLength: number;
}

export interface GetNextTrackIndexResult {
  nextIndex: number;
  newShuffledIndices?: number[];
}

export function getNextTrackIndex(params: GetNextTrackIndexParams): GetNextTrackIndexResult | null {
  const { shuffleMode, repeatMode, currentIndex, shuffledIndices, queueLength } = params;

  if (shuffleMode && shuffledIndices.length > 0) {
    const currentShufflePos = shuffledIndices.indexOf(currentIndex);
    const nextShufflePos = currentShufflePos + 1;
    if (nextShufflePos < shuffledIndices.length) {
      return { nextIndex: shuffledIndices[nextShufflePos] };
    }
    if (repeatMode === 'all') {
      const newShuffled = createShuffledIndices(queueLength, 0);
      return { nextIndex: newShuffled[0], newShuffledIndices: newShuffled };
    }
    return null;
  }

  const nextIndex = currentIndex + 1;
  if (nextIndex >= queueLength) {
    if (repeatMode === 'all') {
      return { nextIndex: 0 };
    }
    return null;
  }
  return { nextIndex };
}

/**
 * Fisher-Yates shuffle algorithm for creating shuffled indices
 */
export function createShuffledIndices(length: number, currentIndex: number): number[] {
  // Create array of indices excluding current
  const indices: number[] = [];
  for (let i = 0; i < length; i++) {
    if (i !== currentIndex) {
      indices.push(i);
    }
  }

  shuffle(indices);

  // Put current index at the front
  return [currentIndex, ...indices];
}
